import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CompanySellerPicker from './CompanySellerPicker';
import {
  assignUnassignedReturnService,
  bulkAssignManualService,
} from '../../../../services/unassignedReturns.service';
import { callUnassigned, isStaleStatus, notifyError } from './unassignedUtils';

const buildRequest = ({ rows, companyId, sellerId, note }) => {
  const trimmedNote = note.trim() || null;
  if (rows.length > 1) {
    return () => bulkAssignManualService({
      packageIds: rows.map((r) => r.PackageId),
      assignedCompanyId: companyId,
      assignedSellerId: sellerId,
      note: trimmedNote,
    });
  }
  return () => assignUnassignedReturnService({
    packageId: rows[0].PackageId,
    assignmentType: 'Manual',
    assignedCompanyId: companyId,
    assignedSellerId: sellerId,
    note: trimmedNote,
  });
};

// Returns what the parent should do next, or null when the request failed and nothing changed.
export const submitManualAssignment = async (args) => {
  const { ok, body, message } = await callUnassigned(buildRequest(args), 'Failed to assign package');
  if (!ok) {
    notifyError(message);
    // The package may already have been assigned by someone else.
    return isStaleStatus(body?.Status) ? { bulk: false, refreshOnly: true } : null;
  }
  if (args.rows.length > 1) return { bulk: true, result: body.Entity };
  if (body.Metadata?.chargeCreated === false) {
    toast.warning('Package assigned, but the seller charge could not be created.');
  }
  toast.success('Assigned. The package moved to Assigned Returns.');
  return { bulk: false };
};

// Single row => Assign endpoint; several rows => BulkAssignManual.
const ManualAssignModal = ({ show, rows, companyOptions, sellerOptions, onHide, onDone }) => {
  const [assignedCompanyId, setAssignedCompanyId] = useState(null);
  const [assignedSellerId, setAssignedSellerId] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setAssignedCompanyId(null);
      setAssignedSellerId(null);
      setNote('');
    }
  }, [show]);

  const isBulk = rows.length > 1;

  const handleSubmit = async () => {
    if (!assignedCompanyId || !assignedSellerId) {
      toast.warning('Please select a company and a seller');
      return;
    }
    setSubmitting(true);
    const outcome = await submitManualAssignment({ rows, companyId: assignedCompanyId, sellerId: assignedSellerId, note });
    setSubmitting(false);
    if (outcome) onDone(outcome);
  };

  return (
    <Modal show={show} onHide={onHide} centered className="modalDeleteWrapper">
      <Modal.Header closeButton>
        <Modal.Title>{isBulk ? `Bulk Assign (${rows.length} packages)` : 'Assign Package Manually'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-info">
          Package(s): <strong>{rows.map((r) => r.trackingNumber).join(', ')}</strong>
        </div>
        <CompanySellerPicker
          companyOptions={companyOptions}
          sellerOptions={sellerOptions}
          companyId={assignedCompanyId}
          sellerId={assignedSellerId}
          onChange={({ companyId, sellerId }) => { setAssignedCompanyId(companyId); setAssignedSellerId(sellerId); }}
        />
        <div>
          <label className="labelHead" htmlFor="unassigned-assign-note">Assignment Note (optional)</label>
          <input
            id="unassigned-assign-note"
            className="form-control"
            value={note}
            maxLength={500}
            placeholder="e.g. Seller confirmed on WhatsApp"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button variant="success" onClick={handleSubmit} disabled={submitting || !assignedCompanyId || !assignedSellerId}>
          {submitting ? 'Assigning...' : 'Assign & Move'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ManualAssignModal.propTypes = {
  show: PropTypes.bool.isRequired,
  rows: PropTypes.array.isRequired,
  companyOptions: PropTypes.array.isRequired,
  sellerOptions: PropTypes.array.isRequired,
  onHide: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default ManualAssignModal;
